import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { DonorSchedule } from '@/rtk-query.gql';
import { Workflow as WorkflowType } from '@/utils/workflow';

import { CLEARABLE_FIELDS } from '../../constants';
import { ValidationIssue } from '../../hooks/useContextMenuActions';
import { ClearableField } from '../../types';
import { SoScheduleWarningDialog } from '../so-schedule-warning-dialog';
import { DonorToDoReportDialog } from '../toolbar/DonorToDoReportDialog';
import { MissingFieldsDialog } from '../toolbar/MissingFieldsDialog';

type ContextMenuDialogsProps = {
  clearFieldDialogVisible: boolean;
  setClearFieldDialogVisible: (visible: boolean) => void;
  selectedField: ClearableField | null;
  setSelectedField: (field: ClearableField | null) => void;
  reportDialogVisible: boolean;
  setReportDialogVisible: (visible: boolean) => void;
  missingFieldsDialogVisible: boolean;
  setMissingFieldsDialogVisible: (visible: boolean) => void;
  soScheduleWarningDialogVisible: boolean;
  setSoScheduleWarningDialogVisible: (visible: boolean) => void;
  soScheduleValidationIssues: ValidationIssue[];
  handleSoScheduleWarningAccept: () => void;
  appSelectedDonorSchedules: DonorSchedule[];
  handleClearField: () => void;
  handlePrintDonorToDoReport: (params: any) => void;
  pendingWorkflow: WorkflowType | null;
};

export const ContextMenuDialogs = ({
  clearFieldDialogVisible,
  setClearFieldDialogVisible,
  selectedField,
  setSelectedField,
  reportDialogVisible,
  setReportDialogVisible,
  missingFieldsDialogVisible,
  setMissingFieldsDialogVisible,
  soScheduleWarningDialogVisible,
  setSoScheduleWarningDialogVisible,
  soScheduleValidationIssues,
  handleSoScheduleWarningAccept,
  appSelectedDonorSchedules,
  handleClearField,
  handlePrintDonorToDoReport,
  pendingWorkflow,
}: ContextMenuDialogsProps) => {
  const { t } = useTranslation();
  const isClearAspirationCommentsEnabled = useFeatureFlag(
    'DS_clear_aspiration_comments',
  );
  const clearableFields = isClearAspirationCommentsEnabled
    ? CLEARABLE_FIELDS
    : CLEARABLE_FIELDS.filter((field) => field.value !== 'specialInstructions');

  const clearFieldDialogFooter = (
    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={() => setClearFieldDialogVisible(false)}
        className="flex items-center gap-2 rounded-md border-none bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200 focus:ring-2 focus:ring-gray-300"
      >
        <i className="pi pi-times" />
        {t('common.no')}
      </button>
      <button
        type="button"
        onClick={handleClearField}
        disabled={!selectedField}
        className="flex items-center gap-2 rounded-md border-none bg-blue-400 px-4 py-2 text-white hover:bg-blue-500 focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <i className="pi pi-check" />
        {t('common.yes')}
      </button>
    </div>
  );

  const renderConfirmationDialogFooter = () => {
    return (
      <>
        <div className="rounded-lg p-3">
          <div className="flex items-center gap-2">
            <i className="pi pi-info-circle mt-0.5"></i>
            <p className="m-0 text-left text-sm">
              {t('donorReport.completeMissingFieldsInstruction')}
            </p>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Dialog
        visible={clearFieldDialogVisible}
        onHide={() => setClearFieldDialogVisible(false)}
        header={t('clearField.clearField')}
        footer={clearFieldDialogFooter}
        className="min-w-[450px]"
        style={{ zIndex: 1000 }}
      >
        <div className="flex flex-col gap-4">
          <p className="m-0 leading-relaxed">
            {t('clearField.selectFieldToClearForSchedules', {
              names: appSelectedDonorSchedules
                ?.map((schedule: DonorSchedule) => schedule.name)
                .join(', '),
            })}
          </p>
          <Dropdown
            value={selectedField}
            options={clearableFields}
            onChange={(e: any) => setSelectedField(e.value)}
            placeholder={t('clearField.selectFieldToClear')}
            className="w-full"
          />
        </div>
      </Dialog>

      <DonorToDoReportDialog
        visible={reportDialogVisible}
        onHide={() => setReportDialogVisible(false)}
        onSubmit={handlePrintDonorToDoReport}
      />

      <MissingFieldsDialog
        visible={missingFieldsDialogVisible}
        onHide={() => setMissingFieldsDialogVisible(false)}
        selectedDonorSchedules={appSelectedDonorSchedules}
        renderFooter={renderConfirmationDialogFooter}
      />

      <SoScheduleWarningDialog
        visible={soScheduleWarningDialogVisible}
        onHide={() => setSoScheduleWarningDialogVisible(false)}
        onAccept={handleSoScheduleWarningAccept}
        validationIssues={soScheduleValidationIssues}
        workflowName={pendingWorkflow?.label}
      />
    </>
  );
};
